class HeaderSlidingAnimation {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupAnimations());
    } else {
      this.setupAnimations();
    }
  }

  setupAnimations() {
    const headings = document.querySelectorAll('.header-sliding-animation');
    
    if (headings.length === 0) return;

    headings.forEach((heading, index) => {
      this.prepareHeading(heading, index);
    });

    // Use Intersection Observer for performance
    this.observeHeadings();
  }

  prepareHeading(heading, headingIndex) {
    const originalHTML = heading.innerHTML;
    
    // Clear the original content
    heading.innerHTML = '';
    heading.style.overflow = 'hidden';
    
    // Create a container for the animated content
    const container = document.createElement('div');
    container.style.position = 'relative';
    
    // Parse HTML and preserve styling
    const wordGroups = this.parseHTMLAndGroupWords(originalHTML, heading, container);
    
    // Create spans for each word with initial hidden state
    wordGroups.forEach((lineWords, lineIndex) => {
      const lineContainer = document.createElement('div');
      lineContainer.style.display = 'flex';
      lineContainer.style.flexWrap = 'wrap';
      
      lineWords.forEach((wordData, wordIndex) => {
        const wordSpan = document.createElement('span');
        wordSpan.textContent = wordData.text;
        wordSpan.style.display = 'inline-block';
        wordSpan.style.marginRight = '0.25em';
        wordSpan.style.transform = 'translateY(100%)';
        wordSpan.style.opacity = '0';
        wordSpan.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s ease';
        wordSpan.dataset.lineIndex = lineIndex;
        wordSpan.dataset.wordIndex = wordIndex;
        wordSpan.dataset.headingIndex = headingIndex;
        
        // Preserve original styling
        if (wordData.element !== 'span') {
          wordSpan.style.fontStyle = wordData.element === 'em' || wordData.element === 'i' ? 'italic' : 'normal';
          wordSpan.style.fontWeight = wordData.element === 'strong' || wordData.element === 'b' ? 'bold' : 'normal';
        }
        if (wordData.className) {
          wordSpan.className = wordData.className;
        }
        if (wordData.style) {
          wordSpan.style.cssText += wordData.style;
        }
        
        lineContainer.appendChild(wordSpan);
      });
      
      container.appendChild(lineContainer);
    });
    
    heading.appendChild(container);
    heading.dataset.animated = 'false';
  }

  parseHTMLAndGroupWords(html, heading, container) {
    // Create a temporary div to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Temporarily add container to measure word positions
    heading.appendChild(container);
    
    const wordGroups = [];
    let currentLine = [];
    let lastTop = null;
    
    // Function to extract words with their styling
    const extractWordsWithStyling = (element) => {
      const words = [];
      
      // Recursively process child nodes
      for (let node of element.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          // Text node - split into words
          const textWords = node.textContent.trim().split(/\s+/);
          textWords.forEach(word => {
            if (word) {
              words.push({
                text: word,
                element: element.tagName.toLowerCase(),
                className: element.className,
                style: element.style.cssText
              });
            }
          });
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Element node - recursively process
          words.push(...extractWordsWithStyling(node));
        }
      }
      
      return words;
    };
    
    const wordsWithStyling = extractWordsWithStyling(tempDiv);
    
    // Group words by their visual lines
    wordsWithStyling.forEach(wordData => {
      const tempSpan = document.createElement('span');
      tempSpan.textContent = wordData.text + ' ';
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      container.appendChild(tempSpan);
      
      const rect = tempSpan.getBoundingClientRect();
      const currentTop = Math.round(rect.top);
      
      if (lastTop !== null && currentTop > lastTop) {
        // New line detected
        wordGroups.push([...currentLine]);
        currentLine = [wordData];
      } else {
        currentLine.push(wordData);
      }
      
      lastTop = currentTop;
      container.removeChild(tempSpan);
    });
    
    // Add the last line
    if (currentLine.length > 0) {
      wordGroups.push(currentLine);
    }
    
    // Remove temporary container
    heading.removeChild(container);
    
    return wordGroups;
  }

  observeHeadings() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.target.dataset.animated === 'false') {
          this.animateHeading(entry.target);
          entry.target.dataset.animated = 'true';
        }
      });
    }, {
      threshold: 0.2,
      rootMargin: '0px 0px -10% 0px'
    });

    document.querySelectorAll('.header-sliding-animation').forEach(heading => {
      observer.observe(heading);
    });
  }

  animateHeading(heading) {
    const words = heading.querySelectorAll('span[data-word-index]');
    
    // Group words by their position in the line (first words, second words, etc.)
    const wordsByPosition = {};
    
    words.forEach(word => {
      const wordIndex = parseInt(word.dataset.wordIndex);
      if (!wordsByPosition[wordIndex]) {
        wordsByPosition[wordIndex] = [];
      }
      wordsByPosition[wordIndex].push(word);
    });
    
    // Animate words by position with staggered timing
    Object.keys(wordsByPosition).forEach((position, index) => {
      const wordsAtPosition = wordsByPosition[position];
      
      setTimeout(() => {
        wordsAtPosition.forEach((word, wordIndex) => {
          setTimeout(() => {
            word.style.transform = 'translateY(0)';
            word.style.opacity = '1';
          }, wordIndex * 30); // Small stagger between words at same position
        });
      }, 200 + (index * 80)); // 0.2s initial delay + main delay between word positions
    });
  }

  // Method to manually trigger animation (useful for testing)
  triggerAnimation(selector = '.header-sliding-animation') {
    const headings = document.querySelectorAll(selector);
    headings.forEach(heading => {
      if (heading.dataset.animated === 'false') {
        this.animateHeading(heading);
        heading.dataset.animated = 'true';
      }
    });
  }

  // Method to reset animations
  resetAnimations(selector = '.header-sliding-animation') {
    const headings = document.querySelectorAll(selector);
    headings.forEach(heading => {
      const words = heading.querySelectorAll('span[data-word-index]');
      words.forEach(word => {
        word.style.transform = 'translateY(100%)';
        word.style.opacity = '0';
      });
      heading.dataset.animated = 'false';
    });
  }
}

// Initialize the animation system
const headerAnimation = new HeaderSlidingAnimation();

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HeaderSlidingAnimation;
}